import { type FC, useState } from 'react';
import StyleIcon from '@mui/icons-material/Style';
import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { DEFAULT_LABEL_COLOR_TOKEN } from '../../pastelPalette';
import {
  useCreateLabelGroupMutation,
  useCreateLabelMutation,
  useDeleteLabelGroupMutation,
  useDeleteLabelMutation,
  useGetLabelGroupsQuery,
  useGetLabelsQuery,
  useMoveLabelMutation,
  useUpdateLabelGroupMutation,
  useUpdateLabelMutation,
} from '../../store/api';
import type { Label, LabelGroup } from '../../types';
import { EmptyLabelsState } from '../EmptyLabelsState/EmptyLabelsState';
import { LabelColorSelect } from '../LabelColorSelect/LabelColorSelect';
import { LabelGroupRow } from '../LabelGroupRow/LabelGroupRow';
import { LabelRow } from '../LabelRow/LabelRow';
import { LabelsToolbar } from '../LabelsToolbar/LabelsToolbar';

export interface AppointmentLabelsManagerProps {
  organizationId: string;
}

type CreateMode = 'group' | 'label' | null;

interface VisibleGroup {
  group: LabelGroup;
  labels: Label[];
}

/** Pull a human-readable "(status) message" out of an RTK Query error. */
const describeApiError = (err: unknown): string => {
  const e = err as
    | { status?: number; data?: { status?: number; message?: string; error?: string } }
    | undefined;
  const status = e?.status ?? e?.data?.status;
  const message = e?.data?.message ?? e?.data?.error;
  if (status && message) return `(${status}) ${message}`;
  if (status) return `(${status})`;
  return message ?? '';
};

const emptyLabelDraft = {
  name: '',
  color: DEFAULT_LABEL_COLOR_TOKEN,
  groupId: '',
};

/**
 * Practice admin surface for appointment labels (GDN-2019), modeled on Linear's
 * Project labels: a filter + "New group"/"New label" toolbar, inline row
 * creation, and a collapsible group → indented-label tree with per-row rename /
 * move / delete. This is the container — it owns data + state; the rows, toolbar
 * and empty state are presentational.
 */
export const AppointmentLabelsManager: FC<AppointmentLabelsManagerProps> = ({
  organizationId,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { appointmentLabels: tracking } = useEventTracking();
  const orgArg = { orgId: organizationId };
  const { data: groupsData } = useGetLabelGroupsQuery(orgArg);
  const { data: labelsData } = useGetLabelsQuery(orgArg);

  const [createGroup] = useCreateLabelGroupMutation();
  const [createLabel] = useCreateLabelMutation();
  const [updateGroup] = useUpdateLabelGroupMutation();
  const [updateLabel] = useUpdateLabelMutation();
  const [moveLabel] = useMoveLabelMutation();
  const [deleteGroup] = useDeleteLabelGroupMutation();
  const [deleteLabel] = useDeleteLabelMutation();

  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [createMode, setCreateMode] = useState<CreateMode>(null);
  const [groupDraft, setGroupDraft] = useState('');
  const [labelDraft, setLabelDraft] = useState(emptyLabelDraft);

  const groups: LabelGroup[] = groupsData?.items ?? [];
  const labels: Label[] = labelsData?.items ?? [];

  const runMutation = async (
    action: () => Promise<unknown>,
    errorMsg: string,
    onSuccess?: () => void,
  ) => {
    try {
      await action();
      onSuccess?.();
    } catch (err) {
      const detail = describeApiError(err);
      enqueueSnackbar(detail ? `${errorMsg} ${detail}` : errorMsg, {
        variant: 'error',
      });
    }
  };

  // One Statsig event for every catalog mutation; `action` carries the verb.
  const trackCatalog = (
    action: Parameters<typeof tracking.catalogChanged>[0]['action'],
    extra?: { in_group?: boolean; moved_to_group?: boolean },
  ) => tracking.catalogChanged({ action, organization_id: organizationId, ...extra });

  const cancelCreate = () => {
    setCreateMode(null);
    setGroupDraft('');
    setLabelDraft(emptyLabelDraft);
  };

  const openCreateGroup = () => {
    setGroupDraft('');
    setCreateMode('group');
  };

  const openCreateLabel = (groupId = '') => {
    setLabelDraft({ ...emptyLabelDraft, groupId });
    setCreateMode('label');
  };

  const submitGroup = async () => {
    const name = groupDraft.trim();
    if (!name) return;
    await runMutation(
      () => createGroup({ orgId: organizationId, name }).unwrap(),
      'Could not create group.',
      () => trackCatalog('group_created'),
    );
    cancelCreate();
  };

  const submitLabel = async () => {
    const name = labelDraft.name.trim();
    if (!name) return;
    await runMutation(
      () =>
        createLabel({
          orgId: organizationId,
          name,
          color: labelDraft.color,
          label_group_id: labelDraft.groupId || null,
        }).unwrap(),
      'Could not create label.',
      () => trackCatalog('label_created', { in_group: labelDraft.groupId !== '' }),
    );
    cancelCreate();
  };

  const toggleCollapse = (groupId: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const normalized = filter.trim().toLowerCase();
  const nameMatches = (name: string) => name.toLowerCase().includes(normalized);
  const labelsByGroup = (groupId: string) =>
    labels.filter(l => l.label_group_id === groupId);

  const visibleGroups: VisibleGroup[] = groups.flatMap(group => {
    const children = labelsByGroup(group.label_group_id);
    if (!normalized || nameMatches(group.name)) {
      return [{ group, labels: children }];
    }
    const matching = children.filter(l => nameMatches(l.name));
    return matching.length ? [{ group, labels: matching }] : [];
  });

  const standalone = labels.filter(l => !l.label_group_id);
  const visibleStandalone = normalized
    ? standalone.filter(l => nameMatches(l.name))
    : standalone;

  const hasData = groups.length > 0 || labels.length > 0;
  const hasVisible = visibleGroups.length > 0 || visibleStandalone.length > 0;

  const renderLabelRow = (label: Label, indented: boolean) => (
    <LabelRow
      key={label.label_id}
      label={label}
      groups={groups}
      indented={indented}
      onRename={name =>
        void runMutation(
          () =>
            updateLabel({
              orgId: organizationId,
              labelId: label.label_id,
              name,
            }).unwrap(),
          'Could not rename label.',
          () => trackCatalog('label_renamed'),
        )
      }
      onMove={groupId =>
        void runMutation(
          () =>
            moveLabel({
              orgId: organizationId,
              labelId: label.label_id,
              label_group_id: groupId,
            }).unwrap(),
          'Could not move label.',
          () => trackCatalog('label_moved', { moved_to_group: Boolean(groupId) }),
        )
      }
      onDelete={() =>
        void runMutation(
          () =>
            deleteLabel({
              orgId: organizationId,
              labelId: label.label_id,
            }).unwrap(),
          'Could not delete label.',
          () => trackCatalog('label_deleted'),
        )
      }
    />
  );

  return (
    <Box>
      <LabelsToolbar
        filter={filter}
        onFilterChange={setFilter}
        onNewGroup={openCreateGroup}
        onNewLabel={() => openCreateLabel()}
      />

      {createMode === 'group' && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 1 }}>
          <StyleIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          <TextField
            autoFocus
            size="small"
            placeholder="Group name"
            value={groupDraft}
            onChange={e => setGroupDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void submitGroup();
              if (e.key === 'Escape') cancelCreate();
            }}
            sx={{ flex: 1 }}
          />
          <Button variant="contained" onClick={() => void submitGroup()}>
            Add
          </Button>
          <Button onClick={cancelCreate}>Cancel</Button>
        </Box>
      )}

      {createMode === 'label' && (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            py: 1,
            flexWrap: 'wrap',
          }}
        >
          <TextField
            autoFocus
            size="small"
            placeholder="Label name"
            value={labelDraft.name}
            onChange={e =>
              setLabelDraft(d => ({ ...d, name: e.target.value }))
            }
            onKeyDown={e => {
              if (e.key === 'Enter') void submitLabel();
              if (e.key === 'Escape') cancelCreate();
            }}
            sx={{ flex: 1, minWidth: 180 }}
          />
          <LabelColorSelect
            value={labelDraft.color}
            onChange={color => setLabelDraft(d => ({ ...d, color }))}
          />
          <TextField
            select
            size="small"
            label="Group"
            value={labelDraft.groupId}
            onChange={e =>
              setLabelDraft(d => ({ ...d, groupId: e.target.value }))
            }
            sx={{ width: 160 }}
          >
            <MenuItem value="">Standalone</MenuItem>
            {groups.map(group => (
              <MenuItem key={group.label_group_id} value={group.label_group_id}>
                {group.name}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" onClick={() => void submitLabel()}>
            Add
          </Button>
          <Button onClick={cancelCreate}>Cancel</Button>
        </Box>
      )}

      {!hasData && createMode === null && <EmptyLabelsState />}

      {hasData && !hasVisible && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 3, textAlign: 'center' }}
        >
          No labels match “{filter}”.
        </Typography>
      )}

      <Stack spacing={0.25} sx={{ mt: 1 }}>
        {visibleGroups.map(({ group, labels: groupLabels }) => (
          <LabelGroupRow
            key={group.label_group_id}
            group={group}
            labelCount={groupLabels.length}
            collapsed={collapsed.has(group.label_group_id)}
            onToggleCollapse={() => toggleCollapse(group.label_group_id)}
            onRename={name =>
              void runMutation(
                () =>
                  updateGroup({
                    orgId: organizationId,
                    labelGroupId: group.label_group_id,
                    name,
                  }).unwrap(),
                'Could not rename group.',
                () => trackCatalog('group_renamed'),
              )
            }
            onAddLabel={() => openCreateLabel(group.label_group_id)}
            onDelete={() =>
              void runMutation(
                () =>
                  deleteGroup({
                    orgId: organizationId,
                    labelGroupId: group.label_group_id,
                  }).unwrap(),
                'Could not delete group.',
                () => trackCatalog('group_deleted'),
              )
            }
          >
            {groupLabels.map(label => renderLabelRow(label, true))}
          </LabelGroupRow>
        ))}

        {visibleStandalone.length > 0 && (
          <Box sx={{ mt: visibleGroups.length ? 1.5 : 0 }}>
            {visibleGroups.length > 0 && (
              <Typography variant="overline" color="text.secondary">
                Standalone
              </Typography>
            )}
            {visibleStandalone.map(label => renderLabelRow(label, false))}
          </Box>
        )}
      </Stack>
    </Box>
  );
};
